/**
 * Minimal hover/focus tooltip primitive. No external dependency —
 * the app's other primitives (Tabs, Dialog, etc.) are hand-rolled in
 * the same style, so adding `@radix-ui/react-tooltip` for one
 * use-site (chart-title (i) icons on the Analytics page) isn't
 * worth the dep weight.
 *
 * Accessibility:
 *   - The trigger renders as a real `<button>`, so the (i) icon
 *     is keyboard-focusable and the hover-vs-focus distinction is
 *     real for keyboard users. `children` becomes the visible label
 *     inside the button (typically the lucide `Info` icon).
 *   - The popover gets a stable `id` (via `useId()`); when open, the
 *     button gets `aria-describedby` pointing at that id, so screen
 *     readers announce the tooltip content on focus.
 *   - `role="tooltip"` on the popover.
 *   - Default `side='bottom'` so the popover drops below the
 *     trigger and the chart title stays unobscured.
 *
 * Scope deliberately tight: no `delayDuration` knob and no smart
 * positioning in place. Good enough for "info icon next to a chart
 * title"; promote to a richer primitive when a second use site demands
 * it. The one exception is `portal`, opt-in, for a trigger inside a
 * scrolling container such as a table cell, which would otherwise cut
 * the tooltip off at its edge.
 */
import { useEffect, useId, useState, useRef, useLayoutEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

interface TooltipProps {
  /** Tooltip content. Plain text or a rich ReactNode. */
  content: ReactNode
  /** The trigger contents — typically the `(i)` icon. Rendered inside
   * a `<button>` for keyboard focus, so the icon itself does NOT
   * need to be tabbable. */
  children: ReactNode
  /** Which side of the trigger to render on. Default `bottom` — drops
   * below the trigger so the (i) icon stays clearly visible above
   * its explanation and the chart title isn't obscured. */
  side?: 'top' | 'bottom'
  /** Extra classes on the popover. */
  className?: string
  /** Accessible label for the button when the icon alone doesn't
   * convey purpose. Defaults to `'More info'`. */
  triggerLabel?: string
  /** Horizontal placement of the popover relative to the trigger.
   * `center` (default) centres it. `start` aligns the popover's
   * left edge to the trigger — use it for a trigger near a
   * container's left edge so a wide popover opens inward instead
   * of off the edge (where an `overflow` ancestor clips it). `end`
   * mirrors that on the right. No automatic flip-when-clipped — the
   * caller picks the safe side. */
  align?: 'center' | 'start' | 'end'
  /** Render the open tooltip at the page level, pinned to the trigger,
   * instead of inside it. Needed wherever the trigger sits in a scrolling
   * container (a table cell): there the tooltip would otherwise be cut off
   * at the container's edge. It opens on the other side when there is no
   * room on `side`, and stays inside the viewport. Same approach as
   * `KebabMenu`'s `portal`. */
  portal?: boolean
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
  align = 'center',
  className,
  triggerLabel = 'More info',
  portal = false,
}: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  /** Where the page-level tooltip sits, measured from the trigger. */
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null)
  // Horizontal collision clamp. The popover anchors to a fixed side
  // (`align`), and there is no flip-when-clipped — so a popover near a
  // viewport (or clipping container) edge gets cut off. After it opens we
  // measure it and shift it horizontally just enough to stay on screen.
  // The shift is 0 whenever it already fits, so this never moves a
  // tooltip that wasn't clipping. Replaces the per-call "pick the safe
  // side" guesswork that broke when a card changed columns responsively
  // (#1614 follow-up). Runs in a layout effect so the shift lands before
  // paint — no visible jump.
  const popRef = useRef<HTMLSpanElement>(null)
  const [shiftX, setShiftX] = useState(0)
  useLayoutEffect(() => {
    if (!open) {
      // Reset so the next open re-measures from the natural position.
      if (shiftX !== 0) setShiftX(0)
      return
    }
    // A page-level tooltip is placed whole by the effect below.
    if (portal) return
    const el = popRef.current
    if (!el) return
    const margin = 8
    const rect = el.getBoundingClientRect()
    // width 0 = not laid out (e.g. jsdom in tests) — nothing to measure.
    if (rect.width === 0) return
    // Clamp within the nearest CLIPPING ancestor (a dialog body with
    // overflow:auto, a scroll area, …), not just the viewport. A centered
    // dialog narrower than the viewport would otherwise let the popover
    // spill past the dialog's edge — still inside the viewport, so the
    // viewport-only clamp never moved it — and get cut off by the dialog's
    // overflow. Falls back to the viewport when there is no clipping
    // ancestor (e.g. a chart-title tooltip on an open page).
    let leftBound = margin
    let rightBound = window.innerWidth - margin
    for (let node = el.parentElement; node; node = node.parentElement) {
      const o = getComputedStyle(node)
      if (o.overflowX !== 'visible' || o.overflowY !== 'visible') {
        const cr = node.getBoundingClientRect()
        leftBound = Math.max(leftBound, cr.left + margin)
        rightBound = Math.min(rightBound, cr.right - margin)
        break
      }
    }
    // Wider than the available band → clamping can't win; leave it
    // anchored rather than oscillate between the two clipped edges.
    if (rect.width >= rightBound - leftBound) {
      if (shiftX !== 0) setShiftX(0)
      return
    }
    // `rect` already includes the current shift, so adjust relative to it.
    let next = shiftX
    if (rect.left < leftBound) next = shiftX + (leftBound - rect.left)
    else if (rect.right > rightBound) next = shiftX - (rect.right - rightBound)
    if (next !== shiftX) setShiftX(next)
  }, [open, shiftX, portal])

  // Page-level placement: measured after the tooltip renders, so its own
  // size decides whether it fits on `side` or has to open on the other one,
  // and where it can sit horizontally without leaving the viewport. A
  // layout effect, so it moves into place before paint.
  useLayoutEffect(() => {
    if (!portal || !open) { setPlace(null); return }
    const btn = triggerRef.current?.getBoundingClientRect()
    const el = popRef.current
    if (!btn || !el) return
    const margin = 8
    const gap = 6
    const { width, height } = el.getBoundingClientRect()
    const fitsBelow = btn.bottom + gap + height <= window.innerHeight - margin
    const fitsAbove = btn.top - gap - height >= margin
    const above = side === 'top' ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove
    const ideal =
      align === 'start' ? btn.left : align === 'end' ? btn.right - width : btn.left + btn.width / 2 - width / 2
    setPlace({
      top: above ? btn.top - gap - height : btn.bottom + gap,
      left: Math.min(Math.max(margin, ideal), Math.max(margin, window.innerWidth - margin - width)),
    })
  }, [portal, open, side, align])

  // A page-level tooltip does not move with the page, so close it on scroll
  // or resize rather than leave it pointing at the wrong row.
  useEffect(() => {
    if (!portal || !open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [portal, open])

  // Center alignment carries its own -50% base; fold the clamp shift into
  // one transform so the two don't fight (a className `-translate-x-1/2`
  // would be overridden by an inline `transform`).
  const transform =
    align === 'center'
      ? `translateX(calc(-50% + ${shiftX}px))`
      : `translateX(${shiftX}px)`
  // `max-w-md` (28rem) makes the popover wide rather than tall — long lines
  // wrap less aggressively, which is easier to scan than a narrow vertical
  // column of text.
  const look =
    'z-50 px-3 py-2 rounded-md bg-popover text-popover-foreground text-xs leading-relaxed shadow-md border w-max max-w-md whitespace-normal pointer-events-none'
  const popover = !open
    ? null
    : portal
      ? (
        <span
          ref={popRef}
          id={id}
          role="tooltip"
          // Off screen until measured; the layout effect places it before paint.
          style={{ position: 'fixed', top: place?.top ?? -9999, left: place?.left ?? -9999 }}
          className={cn(look, className)}
        >
          {content}
        </span>
      )
      : (
        <span
          ref={popRef}
          id={id}
          role="tooltip"
          style={{ transform }}
          className={cn(
            'absolute',
            look,
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            // The center base offset moves into the inline transform above;
            // here we only set the anchor edge.
            align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2',
            className,
          )}
        >
          {content}
        </span>
      )
  return (
    <span className="relative inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-describedby={open ? id : undefined}
        // No background / border on the button — the visible affordance
        // is the icon inside. `focus-visible` keeps a focus ring only
        // for keyboard navigation, not mouse clicks.
        className="inline-flex items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </button>
      {/* `createPortal` needs a real document; fall back to in place without one. */}
      {portal && popover && typeof document !== 'undefined' ? createPortal(popover, document.body) : popover}
    </span>
  )
}
